  if (!customElements.get('product-tabs-modal')) {
    class ProductTabsModal extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.dialog = this.querySelector('.product-tabs-modal__dialog');
        this.content = this.querySelector('[data-product-tabs-modal-content]');
        this.closeButton = this.querySelector('[data-product-tabs-modal-close]');
        this.productUrl = this.dataset.productUrl;

        this.statusRegion = document.createElement('p');
        this.statusRegion.className = 'visually-hidden';
        this.statusRegion.setAttribute('role', 'status');
        this.dialog.appendChild(this.statusRegion);

        this.closeButton.addEventListener('click', () => this.hide());
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.hide();
        });
        this.content.addEventListener('change', (event) => {
          const variantSelect = event.target.closest('variant-selects, variant-radios');
          if (!variantSelect) return;
          setTimeout(() => {
            this.syncSelectedVariantMedia(variantSelect);
            this.updateMediaHeight();
          });
        });
        this.handleResize = () => this.updateMediaHeight();
        window.addEventListener('resize', this.handleResize);

        if (!this.movedToBody) {
          this.movedToBody = true;
          document.body.appendChild(this);
        }
      }

      show(opener) {
        const productUrl = opener && opener.dataset.productUrl;
        const productHandle = opener && opener.dataset.productHandle;
        if (productUrl) this.productUrl = productUrl;
        if (productHandle) this.dataset.productHandle = productHandle;
        if (!this.productUrl) return;

        this.openedBy = opener;
        this.productTitle = (opener && opener.dataset.productTitle) || '';
        this.setButtonLoading(true);
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this.dialog.setAttribute('aria-busy', 'true');
        this.announce(this.productTitle ? `Loading ${this.productTitle}` : 'Loading product');

        if (typeof trapFocus === 'function') {
          trapFocus(this, this.dialog);
        } else {
          this.dialog.focus();
        }

        if (this.loading) return;
        this.loading = true;
        this.renderLoadingState();
        this.fetchController = new AbortController();

        fetch(this.productUrl, { signal: this.fetchController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            if (!productElement) throw new Error('Main product row was not found.');

            this.prepareProductElement(productElement);
            this.setInnerHTML(this.content, productElement.innerHTML);
            this.insertAppstleSelector();
            this.movePriceAfterVariantControls();
            this.initializeProductMedia();
            this.observeProductInfo();
            this.loadedUrl = this.productUrl;
            this.finishOpening();
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
            this.content.innerHTML = `
              <div class="product-tabs-modal__error">
                <p>Product options could not be loaded.</p>
                <a class="button button--primary" href="${this.productUrl}">View product</a>
              </div>
            `;
            this.finishOpening('Product options could not be loaded.');
          });
      }

      hide(preventFocus = false) {
        if (!this.hasAttribute('open')) return;
        this.removeAttribute('open');
        this.setButtonLoading(false);
        this.resetContent();
        this.dialog.removeAttribute('aria-busy');
        this.announce('');

        const openCartDrawer = document.querySelector('cart-drawer.active');
        const returnFocusTo =
          this.openedBy && this.openedBy.isConnected
            ? this.openedBy
            : openCartDrawer && openCartDrawer.querySelector('.drawer__inner');

        if (openCartDrawer && typeof trapFocus === 'function') {
          // Quick Shop sits above the cart drawer; restore the drawer's containment.
          trapFocus(document.getElementById('CartDrawer'), preventFocus ? null : returnFocusTo);
        } else if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(preventFocus ? null : returnFocusTo);
        } else if (!preventFocus && returnFocusTo) {
          returnFocusTo.focus();
        }

        const anotherModalIsOpen = document.querySelector(
          'product-tabs-modal[open], quick-add-modal[open], modal-dialog[open]'
        );
        if (!anotherModalIsOpen) {
          const cartDrawerIsOpen = document.querySelector('cart-drawer.active');
          if (!cartDrawerIsOpen) document.body.classList.remove('overflow-hidden');
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
        }

        if (preventFocus) this.openedBy = null;
      }

      renderLoadingState() {
        this.content.innerHTML = `
          <span class="loading-overlay__spinner">
            <svg
              aria-hidden="true"
              focusable="false"
              role="presentation"
              class="spinner"
              viewBox="0 0 66 66"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </span>
        `;
      }

      resetContent() {
        if (this.fetchController) {
          this.fetchController.abort();
          this.fetchController = null;
        }
        if (this.mediaSlider) {
          this.mediaSlider.destroy(true, true);
          this.mediaSlider = null;
        }
        if (this.productInfoObserver) {
          this.productInfoObserver.disconnect();
          this.productInfoObserver = null;
        }

        this.loading = false;
        this.loadedUrl = null;
        this.renderLoadingState();
      }

      finishOpening(message) {
        this.loading = false;
        this.setButtonLoading(false);
        if (!this.hasAttribute('open')) return;
        this.dialog.removeAttribute('aria-busy');
        this.announce(
          message || (this.productTitle ? `${this.productTitle} options loaded` : 'Product options loaded')
        );
        requestAnimationFrame(() => this.updateMediaHeight());

        if (typeof trapFocus === 'function') {
          trapFocus(this, this.dialog);
        } else {
          this.dialog.focus();
        }
      }

      announce(message) {
        if (this.statusRegion) this.statusRegion.textContent = message;
      }

      setButtonLoading(isLoading) {
        if (!this.openedBy) return;
        this.openedBy.toggleAttribute('aria-disabled', isLoading);
        this.openedBy.classList.toggle('loading', isLoading);
        const spinner = this.openedBy.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      prepareProductElement(productElement) {
        const originalSectionId = productElement.dataset.section;
        const modalSectionId = `product-tabs-${this.id}-${originalSectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(originalSectionId, modalSectionId);
        productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
          variantSelect.dataset.originalSection = originalSectionId;
          variantSelect.dataset.updateUrl = 'false';
        });

        productElement
          .querySelectorAll(
            'pickup-availability, product-modal, .product-info-modal, .customer, .breadcrumb, .product-certifications, .product__read-more'
          )
          .forEach((element) => element.remove());
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        element.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attribute) => {
            newScript.setAttribute(attribute.name, attribute.value);
          });
          newScript.appendChild(document.createTextNode(oldScript.textContent));
          oldScript.replaceWith(newScript);
        });
      }

      insertAppstleSelector() {
        const form = this.content.querySelector('product-form form');
        const buttons = form && form.querySelector('.product-form__buttons');
        if (!form || !buttons || form.querySelector('.appstle_stand_alone_selector')) return;

        const selector = document.createElement('span');
        selector.className = 'appstle_stand_alone_selector';
        selector.dataset.productHandle = this.dataset.productHandle;
        selector.style.display = 'none';

        const initImage = document.createElement('img');
        initImage.src = 'https://ik.imagekit.io/mdclzmx6brh/subscriptions_2afFCHVxa.png';
        initImage.alt = '';
        initImage.setAttribute('onload', 'pollAppstleInit()');
        initImage.style.display = 'none';

        buttons.before(initImage, selector);
      }

      movePriceAfterVariantControls() {
        const price = this.content.querySelector('.price-wrapper[id^="price-"]');
        const controls = this.content.querySelectorAll('.product-form__controls-group');
        const lastControls = controls[controls.length - 1];
        if (!price || !lastControls) return;

        const anchor = lastControls.closest('fieldset') || lastControls;
        anchor.insertAdjacentElement('afterend', price);
      }

      initializeProductMedia() {
        if (typeof Swiper !== 'function') return;
        const sliderElement = this.content.querySelector('.js-media-list');
        if (!sliderElement) return;

        const nextButton = sliderElement.querySelector('.swiper-btn--next');
        const previousButton = sliderElement.querySelector('.swiper-btn--prev');
        const pagination = sliderElement.querySelector('.swiper-pagination');
        const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

        this.mediaSlider = new Swiper(sliderElement, {
          slidesPerView: 1,
          autoHeight: true,
          navigation: {
            nextEl: isRtl ? previousButton : nextButton,
            prevEl: isRtl ? nextButton : previousButton
          },
          pagination: {
            el: pagination,
            clickable: true,
            type: 'bullets',
            renderBullet: (index, className) => {
              return `<span class="${className}"><em>${index}</em><i></i><b></b></span>`;
            }
          },
          on: {
            slideChange: () => {
              if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
            }
          }
        });
      }

      observeProductInfo() {
        if (this.productInfoObserver) this.productInfoObserver.disconnect();
        const productInfo = this.content.querySelector('.product__info-wrapper');
        if (!productInfo || typeof ResizeObserver !== 'function') return;

        this.productInfoObserver = new ResizeObserver(() => this.updateMediaHeight());
        this.productInfoObserver.observe(productInfo);
      }

      updateMediaHeight() {
        const mediaWrapper = this.content.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;

        if (!window.matchMedia('(min-width: 901px)').matches) {
          mediaWrapper.style.removeProperty('--product-tabs-media-height');
          return;
        }

        const buttonsRow = this.content.querySelector('.product-form__buttons-row');
        if (!buttonsRow) return;

        const mediaTop = mediaWrapper.getBoundingClientRect().top;
        const buttonsBottom = buttonsRow.getBoundingClientRect().bottom;
        const mediaHeight = Math.max(0, Math.round(buttonsBottom - mediaTop));

        if (mediaHeight > 0) {
          mediaWrapper.style.setProperty('--product-tabs-media-height', `${mediaHeight}px`);
          if (this.mediaSlider) this.mediaSlider.update();
        }
      }

      syncSelectedVariantMedia(variantSelect) {
        const featuredMedia = variantSelect.currentVariant && variantSelect.currentVariant.featured_media;
        if (!featuredMedia || !this.mediaSlider) return;

        const media = this.content.querySelector(
          `[data-media-id="${variantSelect.dataset.section}-${featuredMedia.id}"]`
        );
        if (!media) return;

        this.mediaSlider.update();
        const slideIndex = Array.from(this.mediaSlider.slides).indexOf(media);
        if (slideIndex >= 0) this.mediaSlider.slideTo(slideIndex);
      }
    }

    customElements.define('product-tabs-modal', ProductTabsModal);
  }
